// ProEpoxy Utility Functions

/**
 * Calculate optimal pack combination for given m² - COST EFFICIENT
 * Uses dynamic programming to find the cheapest combination
 */
export const calculateOptimalPacks = (totalM2, packs) => {
  if (!totalM2 || totalM2 <= 0) return [];
  
  const maxPackSize = Math.max(...packs.map(p => p.size));
  
  // Add buffer for overage - we can go slightly over but not under
  const maxCoverage = totalM2 + maxPackSize;
  
  // DP array: dp[coverage] = { cost, packs: { packSize: count } }
  const dp = new Array(maxCoverage + 1).fill(null);
  dp[0] = { cost: 0, packs: {} };
  
  // Fill DP table
  for (let coverage = 1; coverage <= maxCoverage; coverage++) {
    for (const pack of packs) {
      const prevCoverage = coverage - pack.size;
      if (prevCoverage >= 0 && dp[prevCoverage] !== null) {
        const newCost = dp[prevCoverage].cost + pack.price;
        if (dp[coverage] === null || newCost < dp[coverage].cost) {
          const newPacks = { ...dp[prevCoverage].packs };
          newPacks[pack.size] = (newPacks[pack.size] || 0) + 1;
          dp[coverage] = { cost: newCost, packs: newPacks };
        }
      }
    }
  }
  
  // Find the cheapest option that covers at least totalM2
  let bestCoverage = -1;
  let bestCost = Infinity;
  for (let coverage = totalM2; coverage <= maxCoverage; coverage++) {
    if (dp[coverage] !== null && dp[coverage].cost < bestCost) {
      bestCost = dp[coverage].cost;
      bestCoverage = coverage;
    }
  }
  
  if (bestCoverage === -1) return [];
  
  // Convert to result format. Consumers (Metallic/HighBuild hooks, recipeUtils)
  // read `quantity` — returning `qty` here was silently producing NaN totals.
  const result = [];
  const packMap = dp[bestCoverage].packs;
  for (const pack of packs) {
    if (packMap[pack.size] > 0) {
      result.push({
        ...pack,
        quantity: packMap[pack.size],
        subtotal: pack.price * packMap[pack.size]
      });
    }
  }
  
  return result;
};

/**
 * Format currency in GBP
 */
export const formatPrice = (amount) => {
  return new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: 'GBP' 
  }).format(amount || 0);
};

/**
 * Calculate total m² from areas array
 */
export const calculateTotalM2 = (areas) => {
  return areas.reduce((sum, area) => sum + (parseFloat(area.m2) || 0), 0);
};

// Display label for a pack — kg-based units (e.g. Designer Comfort 12kg / 10m²) show both.
export const packLabel = (p) => (p.kg ? `${p.kg}kg unit · ${p.size}m²` : `${p.size}m² pack`);
